/**
 * Catalog of every public API endpoint documented on the website.
 * Each entry’s `id` is the in-page hash (e.g. /docs/api/events#post-events).
 * Keep this list in sync when adding a new documented endpoint.
 */

export type ApiMethod = 'GET' | 'POST' | 'DELETE' | 'QUERY' | 'MUTATION';

export type ApiEndpointGroup =
  | 'Ingestion'
  | 'Usage'
  | 'Discovery'
  | 'Webhooks'
  | 'GraphQL'
  | 'Forms'
  | 'Support';

export interface ApiEndpoint {
  /** Stable hash slug — unique within its docs page. */
  id: string;
  method: ApiMethod;
  /** Path or GraphQL operation shown in the index. */
  path: string;
  name: string;
  summary: string;
  group: ApiEndpointGroup;
  /** Absolute docs route without hash, e.g. /docs/api/events */
  docsPath: string;
}

export const API_ENDPOINT_GROUPS: ApiEndpointGroup[] = [
  'Ingestion',
  'Usage',
  'Discovery',
  'Webhooks',
  'GraphQL',
  'Forms',
  'Support',
];

export const API_ENDPOINTS: ApiEndpoint[] = [
  {
    id: 'get-form',
    method: 'GET',
    path: '/api/v1/forms/:publicId',
    name: 'Read a published form',
    summary: 'The live questions and settings of a form, to build your own front end.',
    group: 'Forms',
    docsPath: '/docs/api/forms',
  },
  {
    id: 'post-submission',
    method: 'POST',
    path: '/api/v1/forms/:publicId/submissions',
    name: 'Submit answers',
    summary: 'Submit a form from the browser, no key needed. Runs the form steps.',
    group: 'Forms',
    docsPath: '/docs/api/forms',
  },
  {
    id: 'post-upload',
    method: 'POST',
    path: '/api/v1/forms/:publicId/uploads',
    name: 'Upload a file',
    summary: 'Get a signed upload address for one file of a file-upload question.',
    group: 'Forms',
    docsPath: '/docs/api/forms',
  },
  {
    id: 'submit-form',
    method: 'MUTATION',
    path: 'submitForm',
    name: 'Submit a form from your server',
    summary: 'Submit with an API key, with idempotency keys and a higher daily limit.',
    group: 'Forms',
    docsPath: '/docs/api/forms',
  },
  {
    id: 'post-events',
    method: 'POST',
    path: '/api/v1/events',
    name: 'Ingest usage events',
    summary: 'Submit a signed batch of metered usage or commission events.',
    group: 'Ingestion',
    docsPath: '/docs/api/events',
  },
  {
    id: 'post-usage',
    method: 'POST',
    path: '/api/v1/usage',
    name: 'Apply a usage delta',
    summary: 'Add or remove usage with a positive or negative delta.',
    group: 'Usage',
    docsPath: '/docs/guides/usage-reporting',
  },
  {
    id: 'post-usage-set',
    method: 'POST',
    path: '/api/v1/usage/set',
    name: 'Set absolute usage',
    summary: 'Replace the current quantity with an absolute count.',
    group: 'Usage',
    docsPath: '/docs/guides/usage-reporting',
  },
  {
    id: 'post-usage-commission',
    method: 'POST',
    path: '/api/v1/usage/commission',
    name: 'Record a commission sale',
    summary: 'Post a sale amount and transaction id for commission billing.',
    group: 'Usage',
    docsPath: '/docs/guides/usage-reporting',
  },
  {
    id: 'post-usage-batch',
    method: 'POST',
    path: '/api/v1/usage/batch',
    name: 'Batch usage deltas',
    summary: 'Apply up to 100 independent deltas with per-item results.',
    group: 'Usage',
    docsPath: '/docs/guides/usage-reporting',
  },
  {
    id: 'get-usage-state',
    method: 'GET',
    path: '/api/v1/usage/:subscriptionId/:variableId',
    name: 'Read current usage',
    summary: 'Current, peak, and cumulative counts for one subscription meter.',
    group: 'Usage',
    docsPath: '/docs/guides/usage-reporting',
  },
  {
    id: 'get-usage-history',
    method: 'GET',
    path: '/api/v1/usage/:subscriptionId/:variableId/history',
    name: 'Read the usage audit log',
    summary: 'Paginated usage history with the idempotency key behind each entry.',
    group: 'Usage',
    docsPath: '/docs/guides/usage-reporting',
  },
  {
    id: 'get-subscriptions',
    method: 'GET',
    path: '/api/v1/subscriptions',
    name: 'List subscriptions',
    summary: 'Keyset-paginated subscription summaries in the key’s scope.',
    group: 'Discovery',
    docsPath: '/docs/api/external-references',
  },
  {
    id: 'get-subscription',
    method: 'GET',
    path: '/api/v1/subscriptions/:id',
    name: 'Get subscription by id',
    summary: 'Fetch one subscription summary by its Revvly id.',
    group: 'Discovery',
    docsPath: '/docs/api/external-references',
  },
  {
    id: 'get-subscription-by-external',
    method: 'GET',
    path: '/api/v1/subscriptions/by-external/:externalId',
    name: 'Get subscription by external id',
    summary: 'Resolve a subscription summary by your own externalId.',
    group: 'Discovery',
    docsPath: '/docs/api/external-references',
  },
  {
    id: 'get-variables',
    method: 'GET',
    path: '/api/v1/variables',
    name: 'List variables',
    summary: 'Discover meter ids and slugs for usage reporting.',
    group: 'Discovery',
    docsPath: '/docs/api/external-references',
  },
  {
    id: 'get-regions',
    method: 'GET',
    path: '/api/v1/regions',
    name: 'List regions',
    summary: 'List billing regions and their supported currencies.',
    group: 'Discovery',
    docsPath: '/docs/api/external-references',
  },
  {
    id: 'post-invoice-payment',
    method: 'POST',
    path: '/api/v1/webhooks/invoice-payment',
    name: 'Mark invoice paid',
    summary: 'Record a full or partial payment from your payment system.',
    group: 'Webhooks',
    docsPath: '/docs/api/invoice-payments',
  },
  {
    id: 'post-product-events',
    method: 'POST',
    path: '/api/v1/product-events',
    name: 'Send product events',
    summary: 'Report what your users do so Revvly can show how clients use your product.',
    group: 'Ingestion',
    docsPath: '/docs/api/product-events',
  },
  {
    id: 'post-product-events-token',
    method: 'POST',
    path: '/api/v1/product-events/tokens',
    name: 'Mint a browser token',
    summary: 'Give a page a short-lived token to send events for one signed-in person.',
    group: 'Ingestion',
    docsPath: '/docs/api/product-events',
  },
  {
    id: 'post-product-events-browser',
    method: 'POST',
    path: '/api/v1/product-events/browser',
    name: 'Send events from the browser',
    summary: 'Send product events from a page with a token minted for that person.',
    group: 'Ingestion',
    docsPath: '/docs/api/product-events',
  },
  {
    id: 'delete-product-events-user',
    method: 'DELETE',
    path: '/api/v1/product-events/users/:userId',
    name: 'Erase a user’s product events',
    summary: 'Remove every stored product event of one user.',
    group: 'Ingestion',
    docsPath: '/docs/api/product-events',
  },
  {
    id: 'graphql',
    method: 'POST',
    path: '/api/v1/graphql',
    name: 'Public GraphQL endpoint',
    summary: 'Send queries and mutations against the curated public schema.',
    group: 'GraphQL',
    docsPath: '/docs/api/graphql-public',
  },
  {
    id: 'client',
    method: 'QUERY',
    path: 'client',
    name: 'Fetch a client',
    summary: 'Read a client with region, subscriptions, invoices, and receivables.',
    group: 'GraphQL',
    docsPath: '/docs/api/graphql-public',
  },
  {
    id: 'create-prospect',
    method: 'MUTATION',
    path: 'createProspect',
    name: 'Create a prospect',
    summary: 'Submit a form prospect — organization, contact, and lead.',
    group: 'GraphQL',
    docsPath: '/docs/api/graphql-public',
  },
  {
    id: 'create-client',
    method: 'MUTATION',
    path: 'createClient',
    name: 'Create a client',
    summary: 'Provision a billing client in your tenant.',
    group: 'GraphQL',
    docsPath: '/docs/api/graphql-public',
  },
  {
    id: 'update-client',
    method: 'MUTATION',
    path: 'updateClient',
    name: 'Update a client',
    summary: 'Correct a client’s name, externalId, address, tax id or payment terms.',
    group: 'GraphQL',
    docsPath: '/docs/api/graphql-public',
  },
  {
    id: 'create-subscription',
    method: 'MUTATION',
    path: 'createSubscription',
    name: 'Create a subscription',
    summary:
      'Provision a subscription for a client, with a starting quantity per component and its own externalId.',
    group: 'GraphQL',
    docsPath: '/docs/api/graphql-public',
  },
  {
    id: 'existing-subscription',
    method: 'MUTATION',
    path: 'changeSubscriptionPlan',
    name: 'Work with a subscription',
    summary: 'Change its plan or seat count, cancel it, set auto-renew or end its trial.',
    group: 'GraphQL',
    docsPath: '/docs/api/graphql-public',
  },
  {
    id: 'mint',
    method: 'MUTATION',
    path: 'createEndUserToken',
    name: 'Mint a support chat token',
    summary: 'Sign one of your users in to the support chat, from your backend.',
    group: 'Support',
    docsPath: '/docs/guides/support-tokens',
  },
  {
    id: 'support-chat',
    method: 'POST',
    path: '/api/v1/support/graphql',
    name: 'Support chat endpoint',
    summary: 'The chat API a widget calls with an end-user token.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'my-tickets',
    method: 'QUERY',
    path: 'myTickets',
    name: "List a user's requests",
    summary: "The signed-in user's support requests, most recent first.",
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'ticket-messages',
    method: 'QUERY',
    path: 'ticketMessages',
    name: 'Read a conversation',
    summary: 'Messages on one of the user’s requests, paged.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'create-ticket',
    method: 'MUTATION',
    path: 'createTicket',
    name: 'Open a request',
    summary: 'Open a support request with its first message.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'send-message',
    method: 'MUTATION',
    path: 'sendMessage',
    name: 'Reply',
    summary: 'Reply on one of the user’s requests; reopens a closed one.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'mark-read',
    method: 'MUTATION',
    path: 'markRead',
    name: 'Mark as read',
    summary: 'Clear the unread count and cancel the reply email.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'notifications',
    method: 'MUTATION',
    path: 'updateMyNotificationPreferences',
    name: 'Notification preferences',
    summary:
      'Let the user turn the email about unread replies, and progress updates, off or back on.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'close-ticket',
    method: 'MUTATION',
    path: 'closeTicket',
    name: 'Close a request',
    summary: 'Let the user close their own request.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'attachments',
    method: 'POST',
    path: '/api/v1/support/chat/attachments',
    name: 'Upload a chat attachment',
    summary: 'A file for the user to send on a request: images, PDF or text, up to 20 MB.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'updates',
    method: 'GET',
    path: '/api/v1/support/chat/stream',
    name: 'Support chat live updates',
    summary: "Server-sent events for the signed-in user's requests: replies, status, typing.",
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'sync',
    method: 'MUTATION',
    path: 'upsertSupportUsers',
    name: 'Sync support users',
    summary: 'Create or update up to 500 of your users, with attributes, from your backend.',
    group: 'Support',
    docsPath: '/docs/guides/support-tokens',
  },
  {
    id: 'upsert',
    method: 'MUTATION',
    path: 'upsertKnowledgeArticles',
    name: 'Add or update help articles',
    summary: 'Push up to 50 of your help articles, each with the roles and plans that may read it.',
    group: 'Support',
    docsPath: '/docs/guides/support-knowledge-api',
  },
  {
    id: 'delete',
    method: 'MUTATION',
    path: 'deleteKnowledgeArticles',
    name: 'Remove help articles',
    summary: 'Remove up to 200 articles by your own id.',
    group: 'Support',
    docsPath: '/docs/guides/support-knowledge-api',
  },
  {
    id: 'sync',
    method: 'MUTATION',
    path: 'finishKnowledgeSync',
    name: 'Finish a full refresh',
    summary: 'Remove what a full refresh did not send, once every article has been pushed.',
    group: 'Support',
    docsPath: '/docs/guides/support-knowledge-api',
  },
  {
    id: 'request-human',
    method: 'MUTATION',
    path: 'requestHuman',
    name: 'Ask for a person',
    summary: 'Let the user hand a request from the AI assistant to your support team.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
  {
    id: 'rate-answer',
    method: 'MUTATION',
    path: 'rateAnswer',
    name: 'Rate an AI reply',
    summary: 'RESOLVED closes the request as solved; NOT_HELPFUL hands it to a person.',
    group: 'Support',
    docsPath: '/docs/api/support-chat',
  },
];

/** Absolute docs URL including hash for an endpoint. */
export function endpointHref(endpoint: ApiEndpoint): string {
  return `${endpoint.docsPath}#${endpoint.id}`;
}

export function endpointsForPage(docsPath: string): ApiEndpoint[] {
  return API_ENDPOINTS.filter((e) => e.docsPath === docsPath);
}

export function endpointsByGroup(): { group: ApiEndpointGroup; endpoints: ApiEndpoint[] }[] {
  return API_ENDPOINT_GROUPS.map((group) => ({
    group,
    endpoints: API_ENDPOINTS.filter((e) => e.group === group),
  })).filter((g) => g.endpoints.length > 0);
}
